import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { type MapLocation, mapLocationSchema } from '@repo/shared';

/**
 * Saved locations, persisted in localStorage.
 *
 * The MVP has no accounts and no database, so "my places" lives entirely in the
 * browser. Anything read back out is re-validated with Zod — localStorage is
 * user-writable and survives across deploys, so its contents are untrusted
 * input like any other.
 */

/** Versioned so a future shape change can be migrated rather than guessed at. */
const STORAGE_KEY = 'raydario.saved-locations.v1';

/** Keeps the menu scannable and the storage entry small. */
const MAX_SAVED = 12;

export const savedLocationSchema = mapLocationSchema.extend({
  id: z.string().min(1),
});
export type SavedLocation = z.infer<typeof savedLocationSchema>;

const savedLocationsSchema = z.array(savedLocationSchema);

function read(): SavedLocation[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = savedLocationsSchema.safeParse(JSON.parse(raw));
    // Corrupt or outdated data is discarded rather than crashing the app.
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

function write(locations: SavedLocation[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(locations));
  } catch {
    // Private browsing or a full quota — saving is a nicety, not a requirement.
  }
}

/** Two locations are "the same place" if they're within ~10m of each other. */
function isSamePlace(a: MapLocation, b: MapLocation): boolean {
  return Math.abs(a.longitude - b.longitude) < 1e-4 && Math.abs(a.latitude - b.latitude) < 1e-4;
}

export function useSavedLocations() {
  const [locations, setLocations] = useState<SavedLocation[]>([]);

  // Read after mount rather than during render: localStorage is unavailable
  // during SSR/tests and reading it in an initializer would be a side effect.
  useEffect(() => setLocations(read()), []);

  const update = useCallback((next: SavedLocation[]) => {
    setLocations(next);
    write(next);
  }, []);

  const save = useCallback(
    (location: MapLocation) => {
      const next = [
        { ...location, id: crypto.randomUUID() },
        ...locations.filter((saved) => !isSamePlace(saved, location)),
      ].slice(0, MAX_SAVED);
      update(next);
    },
    [locations, update],
  );

  const remove = useCallback(
    (id: string) => update(locations.filter((saved) => saved.id !== id)),
    [locations, update],
  );

  const isSaved = useCallback(
    (location: MapLocation | null) =>
      location !== null && locations.some((saved) => isSamePlace(saved, location)),
    [locations],
  );

  return { locations, save, remove, isSaved };
}
